import { Spacing, Text, Title } from '@vkontakte/vkui';
import coverImageB from 'img/quota-landing/overquota/overqDeleteB.png';
import coverImageC from 'img/quota-landing/overquota/overqDeleteC.png';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { platform } from 'reactApp/appHelpers/configHelpers';
import { overquotaClearDelAb } from 'reactApp/appHelpers/featuresHelpers/features/overquotaClearDelAb';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { QUOTA_URL } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { sendDwh, sendXray } from 'reactApp/utils/ga';

import styles from './OverquotaClearDeleteConfirmDialog.css';

const sendRadar = (action: 'show' | 'click' | 'close' | 'click-reject') => {
    sendXray(['overq_cleaer_delete', platform, action]);
    sendDwh({
        eventCategory: 'modal',
        action,
        dwhData: { name: 'mnz_overquota_cleaner_deletion' },
    });
};

export interface OverquotaClearDeleteConfirmDialogProps {
    onContinue(): void;
}

const isVariantB = overquotaClearDelAb?.variant === 'b';

const text = isVariantB
    ? 'С подпиской Mail Space ничего удалять не надо. 256 ГБ ваши за 1 ₽'
    : 'С подпиской Mail Space ничего удалять не надо. 256 ГБ ваши со скидкой 50%';

const buttonOk = isVariantB ? 'Сохранить за 1 ₽' : 'Сохранить со скидкой';

export const OverquotaClearDeleteConfirmDialog = memo<{ onClose?(): void } & OverquotaClearDeleteConfirmDialogProps>(
    ({ onContinue, onClose }) => {
        const dispatch = useDispatch();
        const isMobile = useSelector(QuotaLandingSelectors.isMobile);
        const scrollRef = useRef<HTMLDivElement>(null);
        const storage = useSelector(getCurrentStorage);

        const handleClose = useCallback(() => {
            sendRadar('close');
            onClose?.();
        }, [onClose]);

        const handleOk = useCallback(() => {
            sendRadar('click');
            onClose?.();

            dispatch(
                openTariffBuy({
                    productId: overquotaClearDelAb?.tariff,
                    isMobile,
                    isQuotaCleaner: true,
                    paySource: getPaySource('modal_overquota_cleaner_deletion'),
                    onError: () => open(QUOTA_URL),
                })
            );
        }, [onClose]);

        const handleSecondary = useCallback(() => {
            sendRadar('click-reject');
            onClose?.();
            onContinue();
        }, [onClose, onContinue]);

        useEffect(() => {
            if (storage !== EStorageType.quotaCleaner) {
                onClose?.();
                return;
            }

            sendRadar('show');
        }, [storage]);

        const content = useMemo(() => {
            return (
                <div className={styles.content}>
                    <Spacing size={12} />
                    <Text className={styles.text}>{text}</Text>
                    <Spacing size={24} />
                </div>
            );
        }, []);

        if (isMobile) {
            return (
                <MobileDialog
                    id="overquota-clear-del-ab"
                    onClose={handleClose}
                    topmost
                    closeOnDimmerClick
                    scrollRef={scrollRef}
                    mod="base"
                    contextPadding="zero"
                    isRebrandingQuotaLanding
                >
                    <div ref={scrollRef} className={styles.rootMobile}>
                        <div className={styles.imageContainer}>
                            <img
                                src={overquotaClearDelAb?.variant === 'b' ? coverImageB : coverImageC}
                                className={styles.image}
                                alt="Автозагрузка без ограничений"
                            />
                        </div>
                        <Spacing size={48} />
                        <Title level="1" className={styles.title}>
                            Удалить файлы — они вам точно не нужны?
                        </Title>
                        <Spacing size={12} />
                        <Text className={styles.text}>{text}</Text>
                        <Spacing size={24} />
                        <div className={styles.buttonWrapper}>
                            <Button theme="vk" primary onClick={handleOk} sizeMode={ButtonSizeMode.big}>
                                {buttonOk}
                            </Button>
                            <Spacing size={12} />
                            <Button theme="vk" onClick={handleSecondary} sizeMode={ButtonSizeMode.big}>
                                Всё равно удалить
                            </Button>
                        </div>
                    </div>
                </MobileDialog>
            );
        }

        return (
            <WhatsNewDialog
                imageUrl={isVariantB ? coverImageB : coverImageC}
                className={styles.root}
                imageClassName={styles.image}
                title="Удалить файлы — они вам точно не нужны?"
                content={content}
                primaryButtonText={buttonOk}
                secondaryButtonText={isVariantB ? 'Всё равно удалить' : 'Удалить'}
                onClick={handleOk}
                onClose={handleClose}
                onSecondary={handleSecondary}
                buttonTheme="octavius"
                dialogSize="tiny"
                qaId="overquota-clear-del-ab"
            />
        );
    }
);

OverquotaClearDeleteConfirmDialog.displayName = 'OverquotaClearDeleteConfirmDialog';
